import React, { useState, useEffect } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import ProtectedButton from "../RBAC/ProtectedButton";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function MilestonesModal({
    show,
    onClose,
    onSave,
    selectedGroup,
    getFileUploadUrl,
    projectValue = 0,
}) {
    const newMilestone = { id: Date.now(), name: "", duration: null, amount: "", description: "", status: 'create', }
    const [milestones, setMilestones] = useState([newMilestone]
    );
    const [uploading, setUploading] = useState(null);

    useEffect(() => {
        if (selectedGroup?.milestones?.length) {
            setMilestones(selectedGroup.milestones);
        }
    }, [selectedGroup]);

    const handleAddMilestone = () => {
        setMilestones([
            ...milestones,
            newMilestone
        ]);
    };

    const handleDelete = (id) => {
        setMilestones((prev) => prev.filter((m) => m.id !== id));
    };

    const handleChange = (id, field, value) => {
        // Convert amount to number to prevent 400 error
        if (field === 'amount') {
            const numericValue = parseFloat(value) || 0;
            setMilestones((prev) =>
                prev.map((m) => (m.id === id ? { ...m, [field]: numericValue } : m))
            );
        } else {
            setMilestones((prev) =>
                prev.map((m) => (m.id === id ? { ...m, [field]: value } : m))
            );
        }
    };

    const handleFileUpload = async (id, file) => {
        if (!file) return;
        try {
            setUploading(id);
            // get pre-signed URL or upload details
            const fileUrl = await getFileUploadUrl(file);

            const updatedFileData = {

                fileUrl,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
            };

            setMilestones((prev) =>
                prev.map((m) => (m.id === id ? { ...m, ...updatedFileData } : m))
            );
        } catch (error) {
            console.error("File upload failed:", error);
        } finally {
            setUploading(null);
        }
    };

    // Calculate total milestone amount
    const milestonesTotal = milestones.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
    const isValidTotal = Math.abs(milestonesTotal - projectValue) < 0.01; // Allow small floating point differences
    const remaining = projectValue - milestonesTotal;

    // Validate all required fields are filled
    const isAllFieldsFilled = milestones.every(m => 
        m.name?.trim() && 
        m.duration && 
        m.amount && 
        parseFloat(m.amount) > 0 &&
        m.description?.trim()
    );

    const isFormValid = isValidTotal && isAllFieldsFilled;

    const handleSave = () => {
        if (!isFormValid) {
            return; // Prevent save if form is not valid
        }
        onSave("Milestones Added", 10, milestones);
        onClose();
    };

    if (!show) return null;

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{
                background: "rgba(0,0,0,0.5)",
                backdropFilter: "blur(4px)",
                zIndex: 1200,
            }}
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-dialog-centered"
                style={{ maxWidth: "900px", width: "90%" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="modal-content p-5 rounded-4 shadow"
                    style={{
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "20px",
                        width: "100%",
                        maxWidth: "900px",
                    }}
                >
                    <h4 className="fw-bold mb-3" style={{ color: "#071437" }}>
                        Project Milestones
                    </h4>

                    {/* Project Value Display */}
                    <div
                        className="p-3 mb-4 rounded-3"
                        style={{
                            background: "#F0F9FF",
                            border: "1px solid #BAE6FD",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-semibold" style={{ color: "#0369A1" }}>
                                Total Project Value:
                            </span>
                            <span className="fw-bold" style={{ color: "#0369A1", fontSize: "1.1rem" }}>
                                ${projectValue.toLocaleString()}
                            </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-semibold" style={{ color: isValidTotal ? "#059669" : "#DC2626" }}>
                                Milestone Total:
                            </span>
                            <span className="fw-bold" style={{ color: isValidTotal ? "#059669" : "#DC2626", fontSize: "1.1rem" }}>
                                ${milestonesTotal.toLocaleString()}
                            </span>
                        </div>
                        {!isValidTotal && (
                            <div
                                className="mt-2 p-2 rounded"
                                style={{
                                    background: remaining > 0 ? "#FEF3C7" : "#FEE2E2",
                                    color: remaining > 0 ? "#92400E" : "#991B1B",
                                    fontSize: "0.9rem"
                                }}
                            >
                                {remaining > 0
                                    ? `⚠️ Remaining to allocate: $${remaining.toLocaleString()}`
                                    : `⚠️ Over allocated by: $${Math.abs(remaining).toLocaleString()}`
                                }
                            </div>
                        )}
                    </div>

                    {/* Milestone Cards */}
                    <div className="d-flex flex-column gap-4">
                        {milestones.map((m, index) => (
                            <div
                                key={m.id}
                                className="p-4 rounded-3"
                                style={{
                                    background: "#F9FAFB",
                                    border: "1px solid #E5E7EB",
                                }}
                            >
                                <div className="fw-semibold mb-3">
                                    Milestone {index + 1}
                                </div>

                                <div className="d-flex gap-3 align-items-start mb-3 flex-wrap">
                                    <button
                                        className="btn btn-outline-light border"
                                        style={{
                                            width: "44px",
                                            height: "44px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <img src="/images/milestone.svg" alt="Reorder" />
                                    </button>

                                    <Form.Control
                                        type="text"
                                        placeholder="Milestone name*"
                                        value={m.name}
                                        onChange={(e) =>
                                            handleChange(m.id, "name", e.target.value)
                                        }
                                        style={{
                                            flex: "1 1 200px",
                                            height: "44px",
                                        }}
                                    />

                                    <DatePicker
                                        selected={m.duration ? new Date(m.duration) : null}
                                        onChange={(date) =>
                                            handleChange(m.id, "duration", date ? date.toISOString() : null)
                                        }
                                        minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                                        dateFormat="dd MMM yyyy"
                                        placeholderText="Select date*"
                                        className="form-control"
                                        style={{ width: "140px", height: "44px" }}
                                        wrapperClassName="date-picker-wrapper"
                                    />

                                    <Form.Control
                                        type="number"
                                        placeholder="$0.00*"
                                        value={m.amount}
                                        onChange={(e) =>
                                            handleChange(m.id, "amount", e.target.value)
                                        }
                                        min="0"
                                        step="0.01"
                                        style={{
                                            width: "120px",
                                            height: "44px",
                                        }}
                                    />

                                    <div className="d-flex align-items-center gap-2">
                                        <label
                                            className="btn border d-flex align-items-center gap-2 position-relative"
                                            style={{
                                                height: "44px",
                                                background: "#fff",
                                                padding: "0 12px",
                                                borderRadius: "8px",
                                                minWidth: "140px",
                                            }}
                                        >
                                            {uploading === m.id ? (
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    variant="primary"
                                                />
                                            ) : (
                                                <>
                                                    <img
                                                        src="/images/files.svg"
                                                        alt="Upload"
                                                        style={{ width: 16, height: 16 }}
                                                    />
                                                    Upload file
                                                    <input
                                                        type="file"
                                                        hidden
                                                        onChange={(e) =>
                                                            handleFileUpload(
                                                                m.id,
                                                                e.target.files[0]
                                                            )
                                                        }
                                                    />
                                                </>
                                            )}
                                        </label>

                                        {/* ✅ Show uploaded file name */}
                                        {m.fileName && (
                                            <span
                                                className="text-muted small text-truncate"
                                                style={{ maxWidth: "150px" }}
                                                title={m.fileName}
                                            >
                                                📎 {m.fileName}
                                            </span>
                                        )}
                                    </div>

                                    <ProtectedButton
                                        permission="updateGroup"
                                        className="btn border-0"
                                        style={{
                                            width: "44px",
                                            height: "44px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "8px",
                                        }}
                                        onClick={() => handleDelete(m.id)}
                                    >
                                        <img src="/images/deleteMilestone.svg" alt="Delete" />
                                    </ProtectedButton>
                                </div>

                                <Form.Control
                                    as="textarea"
                                    placeholder="Milestone description*"
                                    value={m.description}
                                    onChange={(e) =>
                                        handleChange(m.id, "description", e.target.value)
                                    }
                                    rows={2}
                                    style={{
                                        resize: "none",
                                        background: "#fff",
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Add Button */}
                    <div className="mt-4">
                        <ProtectedButton
                            permission="updateGroup"
                            className="btn d-flex align-items-center justify-content-center"
                            style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "8px",
                            }}
                            onClick={handleAddMilestone}
                        >
                            <img src="/images/plusMilestone.svg" alt="Add Milestone" />
                        </ProtectedButton>
                    </div>

                    {/* Footer Buttons */}
                    <div className="d-flex justify-content-end gap-3 mt-5">
                        <ProtectedButton
                            permission="updateGroup"
                            variant="outline-secondary"
                            className="px-4"
                            onClick={onClose}
                            style={{
                                minWidth: "120px",
                                height: "44px",
                                borderRadius: "8px",
                            }}
                        >
                            Cancel
                        </ProtectedButton>
                        <ProtectedButton
                            permission="updateGroup"
                            className="px-4"
                            style={{
                                backgroundColor: isFormValid ? "#181D4C" : "#9CA3AF",
                                color: "#fff",
                                minWidth: "120px",
                                height: "44px",
                                borderRadius: "8px",
                                cursor: isFormValid ? "pointer" : "not-allowed",
                            }}
                            onClick={handleSave}
                            disabled={!isFormValid}
                        >
                            Save
                        </ProtectedButton>
                    </div>
                </div>
            </div>

            {/* Custom DatePicker Styles */}
            <style>{`
                .date-picker-wrapper {
                    width: 140px;
                }
                
                .date-picker-wrapper .react-datepicker-wrapper {
                    width: 100%;
                }
                
                .date-picker-wrapper .react-datepicker__input-container {
                    width: 100%;
                }
                
                .date-picker-wrapper .form-control {
                    height: 44px;
                    border-radius: 8px;
                    border: 1px solid #E5E7EB;
                    padding: 8px 12px;
                    font-size: 14px;
                }
                
                .react-datepicker {
                    border: 1px solid #E5E7EB;
                    border-radius: 8px;
                    font-family: inherit;
                }
                
                .react-datepicker__header {
                    background-color: #F9FAFB;
                    border-bottom: 1px solid #E5E7EB;
                    border-radius: 8px 8px 0 0;
                    padding-top: 12px;
                }
                
                .react-datepicker__current-month {
                    color: #071437;
                    font-weight: 600;
                }
                
                .react-datepicker__day-name {
                    color: #6B7280;
                }
                
                .react-datepicker__day {
                    border-radius: 8px;
                    color: #374151;
                }
                
                .react-datepicker__day:hover {
                    background-color: #F3F4F6;
                    border-radius: 8px;
                }
                
                .react-datepicker__day--selected,
                .react-datepicker__day--keyboard-selected {
                    background-color: #181D4C;
                    color: white;
                    border-radius: 8px;
                }
                
                .react-datepicker__day--disabled {
                    color: #D1D5DB;
                    cursor: not-allowed;
                }
                
                .react-datepicker__navigation {
                    top: 12px;
                }
                
                .react-datepicker__triangle {
                    display: none;
                }
            `}</style>
        </div>
    );
}
